import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const BookingForm = ({ selectedSlot, selectedHelper, onConfirm, onCancel }) => {
  const [formData, setFormData] = useState({
    communicationMethod: selectedSlot?.type || '',
    emailReminder: true,
    smsReminder: true,
    sessionNotes: '',
    privacyMode: false,
    calendarSync: false
  });

  const [errors, setErrors] = useState({});

  const communicationOptions = [
    { value: 'video', label: 'Video Call', description: 'High-quality video session with screen sharing' },
    { value: 'audio', label: 'Audio Call', description: 'Voice-only session for privacy or connectivity' },
    { value: 'phone', label: 'Phone Call', description: 'Traditional phone call to your number' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.communicationMethod) {
      newErrors.communicationMethod = 'Please select a communication method';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const bookingData = {
      ...selectedSlot,
      ...formData,
      helper: selectedHelper,
      bookingId: `BK${Date.now()}`,
      status: 'confirmed'
    };

    onConfirm(bookingData);
  };

  if (!selectedSlot) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Select a Time Slot</h3>
        <p className="text-sm text-muted-foreground">
          Choose an available time slot from the calendar to continue with your booking
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Booking Details</h2>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          onClick={onCancel}
        >
          Clear Selection
        </Button>
      </div>
      <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">{selectedHelper?.name}</h3>
            <p className="text-sm text-muted-foreground mb-2">{selectedHelper?.title}</p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={14} className="text-primary" />
                <span className="text-foreground font-medium">{selectedSlot?.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={14} className="text-primary" />
                <span className="text-foreground font-medium">{selectedSlot?.time}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Timer" size={14} className="text-primary" />
                <span className="text-foreground font-medium">{selectedSlot?.duration} minutes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedHelper?.bookingChargeEnabled && selectedHelper?.bookingChargeAmount && (
        <div className="mb-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Icon name="CreditCard" size={20} className="text-accent" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-foreground mb-1">Booking Charge</h4>
              <p className="text-2xl font-bold text-accent mb-2">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: selectedHelper?.bookingChargeCurrency || 'USD'
                })?.format(selectedHelper?.bookingChargeAmount)}
              </p>
              <p className="text-sm text-muted-foreground">
                This is a one-time charge for booking a session with this Licensed Professional. 
                Payment will be processed securely via Stripe when you confirm your booking.
              </p>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Select
          label="Communication Method"
          description="Choose how you'd like to connect with your helper"
          options={communicationOptions}
          value={formData?.communicationMethod}
          onChange={(value) => handleChange('communicationMethod', value)}
          error={errors?.communicationMethod}
          required
        />

        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground">Reminder Preferences</label>
          <div className="space-y-3">
            <Checkbox
              label="Email Reminder"
              description="Receive email notification 24 hours before session"
              checked={formData?.emailReminder}
              onChange={(e) => handleChange('emailReminder', e?.target?.checked)}
            />
            <Checkbox
              label="SMS Reminder"
              description="Receive text message 1 hour before session"
              checked={formData?.smsReminder}
              onChange={(e) => handleChange('smsReminder', e?.target?.checked)}
            />
          </div>
        </div>

        <Input
          label="Session Notes (Optional)"
          type="text"
          placeholder="Any specific topics or concerns you'd like to discuss"
          value={formData?.sessionNotes}
          onChange={(e) => handleChange('sessionNotes', e?.target?.value)}
          description="These notes will be shared with your helper before the session"
        />

        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground">Privacy Settings</label>
          <div className="space-y-3">
            <Checkbox
              label="Private Session Mode"
              description="Hide this session from your public activity history"
              checked={formData?.privacyMode}
              onChange={(e) => handleChange('privacyMode', e?.target?.checked)}
            />
            <Checkbox
              label="Sync to Calendar"
              description="Add this appointment to your external calendar"
              checked={formData?.calendarSync}
              onChange={(e) => handleChange('calendarSync', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-foreground font-medium mb-1">Privacy & Security</p>
              <p className="text-sm text-muted-foreground">
                All sessions are encrypted end-to-end. Your personal information and session details are protected under HIPAA compliance standards.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            iconName="Check"
            iconPosition="right"
            fullWidth
          >
            Confirm Booking
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;